import type {
  FieldImportSource,
  JiraFieldMapping,
} from "@/lib/types/itsm-config";

/** Where an import creates its Harmony fields. */
export type ImportTarget = "desk" | "global";

/** A provider field as the import picker lists it (itsm-api
 *  `ImportableField`). */
export interface ImportableField {
  field_id: string;
  name: string;
  provider_type?: string | null;
  harmony_type?: string | null;
  allow_multiple?: boolean;
  options?: string[] | null;
  importable: boolean;
  reason?: string | null;
  detail?: string | null;
  mapped_harmony_field_id?: string | null;
  imported?: boolean;
  imported_desk_id?: string | null;
}

export interface ImportableFields {
  fields: ImportableField[];
  incomplete?: boolean;
}

export interface ImportedFieldResult {
  field_id: string;
  harmony_field_id: string;
  label: string;
  harmony_type: string;
  desk_id?: string | null;
  created: boolean;
}

export interface FieldImportResult {
  imported: ImportedFieldResult[];
  skipped: { field_id: string; reason: string; detail: string }[];
}

const HARMONY_TYPE_LABELS: Record<string, string> = {
  text: "Text",
  paragraph: "Paragraph",
  number: "Number",
  date_picker: "Date",
  single_select: "Single select",
  multi_select: "Multi select",
  user_selector: "People",
};

/** A Harmony field type as people read it. */
export function harmonyTypeLabel(field: ImportableField): string {
  if (!field.harmony_type) return "Not supported";
  const base = HARMONY_TYPE_LABELS[field.harmony_type] ?? field.harmony_type;
  return field.harmony_type === "user_selector" && field.allow_multiple
    ? `${base} (several)`
    : base;
}

/**
 * The mapping entries an import saved, as the backend wrote them: each new
 * field paired both ways with its provider field and marked imported. The
 * editor's draft takes them in, so a later Save keeps them.
 */
export function importedMappings(
  result: FieldImportResult,
  projectKey?: string,
): JiraFieldMapping[] {
  return result.imported
    .filter((r) => r.created)
    .map((r) => {
      const imported: FieldImportSource = { desk_id: r.desk_id ?? null };
      if (projectKey) imported.project_key = projectKey;
      return {
        harmony: { kind: "custom", key: r.harmony_field_id },
        target: r.field_id,
        direction: "both",
        imported,
      };
    });
}

/**
 * Existing mappings with an import's entries added. An entry the import
 * replaced - the same provider field, imported before, whose Harmony field
 * was deleted since - gives way to the new one.
 */
export function withImported<T extends { target: string }>(
  existing: T[] | null | undefined,
  added: T[],
): T[] {
  const replaced = new Set(added.map((m) => m.target));
  return [...(existing ?? []).filter((m) => !replaced.has(m.target)), ...added];
}
